import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Paper,
  Typography,
  Grid,
  IconButton,
  Box,
  Button,
} from "@material-ui/core";
import {
  ChevronLeft,
  ChevronRight,
  Today as TodayIcon,
  ArrowBack as ArrowBackIcon,
  GetApp as DownloadIcon,
} from "@material-ui/icons";
// import { fatchCaladerExam } from "../../service/services";
import CustomTableView from "./TableView";
import Loader from "./Loader";
// import { COLORS } from "../Theme/theme";
import axios from "axios";
import { decryptData } from "../../crypto";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Report8Api } from "../../service/services";

const useStyles = makeStyles((theme) => ({
  calendarContainer: {
    padding: theme.spacing(2),
    maxWidth: 320,
    minWidth: 280,
    width: "100%",
    display: "flex",
    flexDirection: "column",
    borderRadius: 16,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
    minHeight: 64,
  },
  monthHeader: {
    textAlign: "center",
    fontWeight: 600,
    color: theme.palette.text.primary,
    flexGrow: 1,
    fontSize: "1rem",
  },
  navButton: {
    padding: theme.spacing(0.5),
    "& svg": {
      fontSize: "1.25rem",
    },
  },
  weekDaysRow: {
    marginBottom: theme.spacing(0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    paddingBottom: theme.spacing(0.5),
  },
  weekDay: {
    textAlign: "center",
    fontWeight: 500,
    color: theme.palette.text.secondary,
    fontSize: "0.75rem",
  },
  calendarGrid: {
    flex: 1,
    minHeight: 200,
  },
  dayRow: {
    minHeight: 32,
  },
  dayCell: {
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  disabledDayCell: {
    cursor: "default",
    pointerEvents: "none",
    opacity: 0.5,
    "&:hover": {
      backgroundColor: "transparent",
    },
  },
  dayNumber: {
    fontSize: "0.75rem",
    width: 26,
    height: 26,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "50%",
    transition: "all 0.2s ease",
  },
  emptyDay: {
    height: 32,
  },
  currentMonthDay: {
    color: theme.palette.text.primary,
  },
  otherMonthDay: {
    color: theme.palette.text.disabled,
  },
  selectedDay: {
    backgroundColor: "#3f51b5",
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: "#3f51b5",
    },
  },
  highlightedDay: {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    fontWeight: 600,
  },
  footer: {
    marginTop: "auto",
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    minHeight: 36,
  },
  footerText: {
    fontSize: "0.7rem",
  },
  todayButton: {
    fontSize: "0.7rem",
    padding: theme.spacing(0.25, 1),
    minWidth: "auto",
    "& .MuiButton-startIcon": {
      marginRight: theme.spacing(0.5),
      "& svg": {
        fontSize: "0.875rem",
      },
    },
  },
}));

// Array of dates to highlight (format: 'YYYY-MM-DD')

const CalendarMUI4 = ({
  highlightDates,
  selectedDate,
  setSelectedDate,
  onViewChange,
}) => {
  const classes = useStyles();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [showTable, setShowTable] = useState(false);
  const [examPapers, setExamPapers] = useState([]);
  const decryptedToken = decryptData(sessionStorage.getItem("token"));

  // Pagination states
  const [pageSize, setPageSize] = useState(25);
  const [onPage, setOnPage] = useState(0);
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState("");
  const [selectedDateForAPI, setSelectedDateForAPI] = useState(null);

  // Download loader state
  const [loaderOpen, setLoaderOpen] = useState(false);

  const navigate = useNavigate();

  const weekDays = ["S", "M", "T", "W", "T", "F", "S"];
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setPage(newPage);
    setOnPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPage(0);
    setOnPage(0);
  };

  // API call function with pagination
  const fetchExamData = async (dateString, pageNumber, size) => {
    if (!dateString || !Number.isFinite(pageNumber) || !Number.isFinite(size))
      return;

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/calendar/date`,
        {
          params: {
            start_date: dateString,
            page: pageNumber + 1,
            limit: size,
          },
          headers: {
            Authorization: `Bearer ${decryptedToken}`,
          },
        },
      );

      if (response.data && response.data.success) {
        setExamPapers(response.data.exams || []);
        setRowCount(response.data.count);
        console.log("Fetched Exam Data:", response.data);
      }
    } catch (error) {
      console.error("Error fetching exams for selected date:", error);
    }
  };

  // Format date to YYYY-MM-DD string
  const formatDateToString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Get days in month
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  // Get first day of month (0 = Sunday, 1 = Monday, etc.)
  const getFirstDayOfMonth = (year, month) => {
    return new Date(year, month, 1).getDay();
  };

  // Navigate to previous month
  const prevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1),
    );
  };

  // Navigate to next month
  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1),
    );
  };

  // Go to today
  const goToToday = () => {
    const today = new Date();
    setCurrentDate(new Date(today.getFullYear(), today.getMonth(), 1));
    setSelectedDate(today);
  };

  // Check if date is selected
  const isSelected = (date) => {
    if (!selectedDate) return;

    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  // Check if date is in the highlight array
  const isHighlighted = (date) => {
    const dateString = formatDateToString(date);
    return highlightDates.includes(dateString);
  };

  // Handle date click
  const handleDateClick = (day, isCurrentMonth) => {
    if (!day) return;

    const clickedDate = new Date(
      currentDate.getFullYear(),
      isCurrentMonth
        ? currentDate.getMonth()
        : day < 15
          ? currentDate.getMonth() + 1
          : currentDate.getMonth() - 1,
      day,
    );

    setSelectedDate(clickedDate);
    handleSelectedDateData(clickedDate);
  };
  // const handleSelectedDateData = async (selectedDate) => {
  //   const dateString = formatDateToString(selectedDate);

  //   const response = await fatchCaladerExam(dateString);
  //   if (response && response.success) {
  //     setExamPapers(response.exam_paper || []);
  //     console.log(response.exam_paper);
  //     setShowTable(true);
  //   }
  //   console.log(response);
  // };

  const handleSelectedDateData = async (selectedDate) => {
    // Date ko 'YYYY-MM-DD' format mein convert karein
    const dateString = formatDateToString(selectedDate);
    setSelectedDateForAPI(dateString);
    setPage(0);
    setOnPage(0);

    // Initial API call
    await fetchExamData(dateString, 0, pageSize);
    setShowTable(true);
    onViewChange(true);
  };

  // useEffect for pagination API calls
  useEffect(() => {
    if (selectedDateForAPI && onPage !== undefined && pageSize !== undefined) {
      fetchExamData(selectedDateForAPI, onPage, pageSize);
    }
  }, [onPage, pageSize, selectedDateForAPI]);

  const handleBackToCalendar = () => {
    setShowTable(false);
    setSelectedDate(null);
    setSelectedDateForAPI(null);
    setPage(0);
    setOnPage(0);
    setRowCount("");
    setExamPapers([]);
    onViewChange(false);
  };

  const startIndex = onPage * pageSize;
  const rows = examPapers.map((d, i) => ({
    srno: startIndex + i + 1,
    id: d.id || startIndex + i, // unique id for DataGrid
    exam_code: d?.exam_code || "N/A",
    // course_name: d?.course?.map((c) => c.course_name).join(", ") || "N/A",
    // batch_name: d?.batch?.map((b) => b.batch_name).join(", ") || "N/A",
    exam_name: d?.exam_name ? d?.exam_name : "N/A",
    start_time: d?.start_time ? d?.start_time : "N/A",
    end_time: d?.end_time ? d?.end_time : "N/A",
    paper_code: d?.paper_code ? d?.paper_code : "N/A",
    paper_uploading_status: d?.question_uploaded ? d?.question_uploaded : "N/A",
    paper_checking_status: d?.paper_check_status
      ? d?.paper_check_status
      : "N/A",
    result_created: d?.created_result ? d?.created_result : "N/A",
    result_generated: d?.genrate_result ? d?.genrate_result : "N/A",
    paper_check_status_details: d?.paper_check_status_details || null,
  }));

  const columns = [
    {
      field: "srno",
      headerName: "S.No",
      width: 70,
      headerClassName: "super-app-theme--header",
      headerAlign: "center",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      renderCell: (params) => params.row.srno,
    },
    {
      field: "exam_code",
      headerName: "Exam Code",
      width: 150,
      // flex: 0.8,
      headerClassName: "super-app-theme--header",
      headerAlign: "center",
      align: "center",
      sortable: false,
      disableColumnMenu: true,

      renderCell: (params) => (
        <span
          style={{
            cursor: "pointer",
            fontWeight: 500,
          }}
          onClick={() => navigate(`/admin/editexam/${params.row.id}`)}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "exam_name",
      headerName: "Exam Name",
      headerClassName: "super-app-theme--header",
      headerAlign: "center",
      width: 300,
      sortable: false,
      disableColumnMenu: true,
      autoPageSize: false,
      renderCell: (params) => (
        <span
          style={{
            cursor: "pointer",
            fontWeight: 500,
          }}
          onClick={() => navigate(`/admin/editexam/${params.row.id}`)}
        >
          {params.value}
        </span>
      ),
    },
    {
      field: "start_time",
      headerName: "Start Time",
      width: 150,
      // flex: 0.8,
      headerClassName: "super-app-theme--header",
      headerAlign: "center",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
    },

    {
      field: "end_time",
      headerName: "End Time",
      width: 150,
      // flex: 0.8,
      headerClassName: "super-app-theme--header",
      headerAlign: "center",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: "paper_code",
      headerName: "Paper Code",
      width: 140,
      // flex: 0.9,
      headerClassName: "super-app-theme--header",
      headerAlign: "center",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
      // renderCell: (params) => params.row.course_name || "N/A",
    },
    {
      field: "paper_uploading_status",
      headerName: "Paper Uploading Status",
      width: 140, // ✅ more base width
      // flex: 2.5, // ✅ takes more remaining space
      headerClassName: "super-app-theme--header",
      headerAlign: "center",
      sortable: false,
      disableColumnMenu: true,
      // renderCell: (params) => params.row.batch_name || "N/A",
    },
    {
      field: "paper_checking_status",
      headerName: "Paper Checking Status",
      width: 180,
      headerClassName: "super-app-theme--header",
      headerAlign: "center",
      align: "center",
      sortable: false,
      disableColumnMenu: true,

      renderCell: (params) => {
        const status = params.row.paper_checking_status;
        const fullName = params.row.paper_check_status_details?.full_name;

        if (status === "yes") {
          return `${status} (${fullName})`;
        }

        return status || "-";
      },
    },

    //  {
    //   field: "server_ready",
    //   headerName: "Server Ready",
    //   width: 150,
    //   // flex: 0.6,
    //   headerClassName: "super-app-theme--header",
    //   headerAlign: "center",
    //   align: "center",
    //   sortable: false,
    //   disableColumnMenu: true,
    // },

    {
      field: "result_created",
      headerName: "Result Created",
      width: 150,
      // flex: 0.6,
      headerClassName: "super-app-theme--header",
      headerAlign: "center",
      align: "center",
      sortable: false,
      disableColumnMenu: true,
    },

    {
      field: "result_generated",
      headerName: "Result Generated",
      width: 150,
      headerClassName: "super-app-theme--header",
      headerAlign: "center",
      align: "center",
      sortable: false,
      disableColumnMenu: true,

      // renderCell: (params) => {
      //   // Value check logic (ensure case-sensitivity matches your API)
      //   const isGenerated = params.value?.toString().toLowerCase() === "yes";

      //   const handleDownload = async (e) => {
      //     // Propagation stop karna zaroori hai taaki row click trigger na ho
      //     e.stopPropagation();

      //     if (!isGenerated) return;

      //     try {
      //       const token = sessionStorage.getItem("token");
      //       if (!token) {
      //         alert("Session expired. Please login again.");
      //         return;
      //       }

      //       const response = await fetch(
      //         `/v1/b2b/exam/export/merge/${params.row.id}`,
      //         {
      //           method: "GET",
      //           headers: {
      //             Authorization: `Bearer ${token}`,
      //           },
      //         }
      //       );

      //       if (!response.ok) throw new Error("Download failed");

      //       const blob = await response.blob();
      //       const url = window.URL.createObjectURL(blob);
      //       const link = document.createElement("a");
      //       link.href = url;
      //       link.download = `exam_result_${params.row.id}.csv`;
      //       document.body.appendChild(link);
      //       link.click();
      //       link.remove();
      //       window.URL.revokeObjectURL(url);

      //     } catch (error) {
      //       console.error("Download error:", error);
      //       alert("Report download karne mein samasya aayi.");
      //     }
      //   };

      //   return (
      //     <div
      //       onClick={handleDownload}
      //       style={{
      //         // Inline style for cursor pointer
      //         cursor: isGenerated ? "pointer" : "not-allowed",
      //         textDecoration: "none",
      //         fontWeight: 500,
      //         width: "100%",
      //         textAlign: "center",
      //         display: "flex",
      //         justifyContent: "center",
      //         alignItems: "center",
      //         height: "100%"
      //       }}
      //     >
      //       {params.value}
      //     </div>
      //   );
      // },

      renderCell: (params) => {
        const isGenerated = params.value?.toString().toLowerCase() === "yes";

        // const handleDownload = async (e) => {
        //   e.stopPropagation();
        //   if (!isGenerated) return;

        //   // ✅ Loader shuru karo — API pending hai
        //   setLoaderOpen(true);

        //   try {
        //     const token = decryptData(sessionStorage.getItem("token"));

        //     const response = await fetch(
        //       `${process.env.REACT_APP_API_BASE_URL}/v1/b2b/exam/export/merge/${params.row.id}`,
        //       {
        //         method: "GET",
        //         headers: {
        //           Authorization: `Bearer ${token}`,
        //         },
        //       }
        //     );

        //     if (!response.ok) throw new Error("Failed");

        //     const data = await response.json();

        //     if (data.success && data.file_url) {
        //       setLoaderOpen(false); // ✅ Loader band karo
        //       window.open(data.file_url, "_blank"); // ✅ New tab mein file kholo
        //     } else {
        //       setLoaderOpen(false); // ✅ Loader band karo
        //       console.warn(data.message || "Download failed");
        //     }

        //   } catch (error) {
        //     setLoaderOpen(false); // ✅ Error par bhi loader band karo
        //     console.error("Download error:", error);
        //   }
        // };
        const handleDownload = async () => {
          try {
            setLoaderOpen(true);

            const response = await Report8Api(params.row.id);

            //   if (response) {
            // // Service level success check
            //     if (response.success === false) {
            //       setLoaderOpen(false);
            //     toast.warning(response.message || "Failed to download the CSV file.");
            //       return;
            //     }

            // // Extract data and content type
            //     const responseData = response.data !== undefined ? response.data : response;
            //     const headers = response.headers || {};
            //     const contentType = headers["content-type"] || (responseData instanceof Blob ? responseData.type : "");

            //     let extension = "csv";
            //     if (contentType.includes("spreadsheetml") || contentType.includes("excel")) {
            //       extension = "xlsx";
            //     } else if (contentType.includes("zip")) {
            //       extension = "zip";
            //     } else if (contentType.includes("pdf")) {
            //       extension = "pdf";
            //     } else if (contentType.includes("text/csv")) {
            //       extension = "csv";
            //     }

            // // Convert to blob if it's not already one
            //     const blob = responseData instanceof Blob ? responseData : new Blob([responseData], { type: contentType || "text/csv;charset=utf-8;" });

            //     const url = window.URL.createObjectURL(blob);

            //     const link = document.createElement("a");
            //     link.href = url;
            // link.download = `${"Report"}.${extension}`;

            //     document.body.appendChild(link);
            //     link.click();

            //     document.body.removeChild(link);
            //     window.URL.revokeObjectURL(url);

            //     toast.success(`${extension.toUpperCase()} downloaded successfully.`);
            //     setLoaderOpen(false);
            //   } else {
            //     toast.error("Failed to download the file.");
            //     setLoaderOpen(false);
            //   }
            console.log(response)
            if (response.success) {
              const fileUrl = response?.file_url;
              console.log("fileUrl",fileUrl)
              const link = document.createElement("a");
              link.href = fileUrl;
              link.download = "export.csv";
              document.body.appendChild(link);
              link.click();
              link.remove();
              setLoaderOpen(false);
            }
          } catch (error) {
            console.error("API Error:", error);
            const errorMessage =
              error.response?.data?.message ||
              "An error occurred while fetching the data.";
            toast.error(errorMessage);
            setLoaderOpen(false);
          }
        };

        return (
          <div
            onClick={isGenerated ? handleDownload : undefined}
            style={{
              cursor: isGenerated ? "pointer" : "default",
              fontWeight: 500,
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "4px",
              height: "100%",
            }}
          >
            {params.value}
            {isGenerated && (
              <DownloadIcon
                title="Download Report"
                style={{ fontSize: "1rem", marginLeft: 2 }}
              />
            )}
          </div>
        );
      },
    },
  ];

  // Generate calendar days (5 weeks only for compact view)
  // const generateCalendarDays = () => {
  //   const year = currentDate.getFullYear();
  //   const month = currentDate.getMonth();

  //   const daysInMonth = getDaysInMonth(year, month);
  //   const firstDayOfMonth = getFirstDayOfMonth(year, month);

  //   // Previous month days
  //   const prevMonthDays = getDaysInMonth(year, month - 1);
  //   const prevMonthStart = prevMonthDays - firstDayOfMonth + 1;

  //   // Calculate total cells for 5 weeks (35 cells)
  //   const totalCells = 35;
  //   const nextMonthDays = totalCells - (firstDayOfMonth + daysInMonth);

  //   const calendarDays = [];

  //   // Add previous month days
  //   for (let i = prevMonthStart; i <= prevMonthDays; i++) {
  //     calendarDays.push({ day: i, isCurrentMonth: false });
  //   }

  //   // Add current month days
  //   for (let i = 1; i <= daysInMonth; i++) {
  //     calendarDays.push({ day: i, isCurrentMonth: true });
  //   }

  //   // Add next month days
  //   for (let i = 1; i <= nextMonthDays; i++) {
  //     calendarDays.push({ day: i, isCurrentMonth: false });
  //   }

  //   // Group into weeks
  //   const weeks = [];
  //   for (let i = 0; i < calendarDays.length; i += 7) {
  //     weeks.push(calendarDays.slice(i, i + 7));
  //   }

  //   return weeks;
  // };
  const generateCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const daysInMonth = getDaysInMonth(year, month);
    const firstDayOfMonth = getFirstDayOfMonth(year, month); // 0 = Sun

    const calendarDays = [];

    // 🔹 Previous month fillers
    const prevMonthDays = getDaysInMonth(year, month - 1);
    for (let i = firstDayOfMonth - 1; i >= 0; i--) {
      calendarDays.push({
        day: prevMonthDays - i,
        isCurrentMonth: false,
      });
    }

    // 🔹 Current month
    for (let i = 1; i <= daysInMonth; i++) {
      calendarDays.push({ day: i, isCurrentMonth: true });
    }

    // 🔹 Next month fillers (to complete last week)
    while (calendarDays.length % 7 !== 0) {
      calendarDays.push({
        day: calendarDays.length - (firstDayOfMonth + daysInMonth) + 1,
        isCurrentMonth: false,
      });
    }

    // 🔹 Split into weeks (auto 5 or 6 rows)
    const weeks = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      weeks.push(calendarDays.slice(i, i + 7));
    }

    return weeks;
  };

  const weeks = generateCalendarDays();
  const style = [
    {
      height: "h89vh",
      style: "viewtable",
      footer: false,
      checkboxselection: false,
      setPageSize: handlePageSizeChange,
      pageSize: pageSize,
      rowCount: rowCount,
      handlePageChange: handlePageChange,
      page: page,
      paginationMode: "server",
    },
  ];
  return (
    <>
      <Loader loaderOpen={loaderOpen} />
      <Paper
        className={classes.calendarContainer}
        elevation={2}
        style={
          showTable
            ? { maxWidth: "100%", minWidth: "100%", height: "100%" }
            : {}
        }
      >
        {showTable ? (
          <>
            <Box className={classes.header}>
              <IconButton onClick={handleBackToCalendar} size="small">
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="subtitle1" className={classes.monthHeader}>
                Exams on {selectedDate.toLocaleDateString("en-GB")}
              </Typography>
            </Box>
            <CustomTableView columns={columns} rows={rows} Heading={style} />
          </>
        ) : (
          <>
            {/* Header with navigation */}
            <Box className={classes.header}>
              <IconButton
                className={classes.navButton}
                onClick={prevMonth}
                size="small"
              >
                <ChevronLeft />
              </IconButton>

              <Typography variant="subtitle1" className={classes.monthHeader}>
                {months[currentDate.getMonth()]} {currentDate.getFullYear()}
              </Typography>

              <IconButton
                className={classes.navButton}
                onClick={nextMonth}
                size="small"
              >
                <ChevronRight />
              </IconButton>
            </Box>

            {/* Week Days Header */}
            <Grid container className={classes.weekDaysRow}>
              {weekDays.map((day, index) => (
                <Grid item xs key={index}>
                  <Typography className={classes.weekDay}>{day}</Typography>
                </Grid>
              ))}
            </Grid>

            {/* Calendar Days */}
            <Box className={classes.calendarGrid}>
              {weeks.map((week, weekIndex) => (
                <Grid container key={weekIndex} className={classes.dayRow}>
                  {week.map(({ day, isCurrentMonth }, dayIndex) => {
                    const date = new Date(
                      currentDate.getFullYear(),
                      isCurrentMonth
                        ? currentDate.getMonth()
                        : day < 15
                          ? currentDate.getMonth() + 1
                          : currentDate.getMonth() - 1,
                      day,
                    );

                    const dayClass = isCurrentMonth
                      ? classes.currentMonthDay
                      : classes.otherMonthDay;

                    const isSelectedFlag = isSelected(date);
                    const isHighlightedFlag = isHighlighted(date);

                    return (
                      <Grid item xs key={dayIndex}>
                        {day ? (
                          <div
                            className={`${classes.dayCell} ${!isHighlightedFlag ? classes.disabledDayCell : ""}`}
                            onClick={() =>
                              isHighlightedFlag &&
                              handleDateClick(day, isCurrentMonth)
                            }
                          >
                            <Typography
                              className={`${classes.dayNumber} ${dayClass} ${
                                isHighlightedFlag ? classes.selectedDay : ""
                              } 
                        `}
                            >
                              {day}
                            </Typography>
                          </div>
                        ) : (
                          <div className={classes.emptyDay} />
                        )}
                      </Grid>
                    );
                  })}
                </Grid>
              ))}
            </Box>
          </>
        )}
      </Paper>
    </>
  );
};

export default CalendarMUI4;
